import { useState, useRef, useEffect } from "react";
import { Car, Wrench, Shield, Phone, Mail, MapPin, ChevronDown, Check, Upload, X, Image, Clock, Star, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIRE_WIDTHS = ["155", "165", "175", "185", "195", "205", "215", "225", "235", "245", "255", "265", "275", "285", "295", "305", "315"];
const ASPECT_RATIOS = ["30", "35", "40", "45", "50", "55", "60", "65", "70", "75", "80"];
const DIAMETERS = ["13", "14", "15", "16", "17", "18", "19", "20", "21", "22"];
const TIRE_TYPES = ["Été", "Hiver", "4 Saisons"];
const TIRE_BRANDS = ["Michelin", "Continental", "Bridgestone", "Pirelli", "Goodyear", "Dunlop", "Hankook", "Nokian", "Vredestein", "Autre"];

const LOGO_URL = "https://customer-assets.emergentagent.com/job_tire-quote-portal/artifacts/nn6a64ea_IMG_1623.png";

export default function HomePage() {
  const [formData, setFormData] = useState({
    customer: {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      postal_code: ""
    },
    vehicle: {
      make: "",
      model: "",
      year: "",
      license_plate: ""
    },
    tires: {
      width: "",
      aspect_ratio: "",
      diameter: "",
      brand_preference: "",
      tire_type: "",
      quantity: 4,
      additional_notes: ""
    },
    services: {
      oil_change: false,
      oil_filter: false,
      air_filter: false,
      cabin_filter: false,
      brake_discs: false,
      brake_pads: false,
      diagnostic: false,
      other_services: ""
    },
    preferred_date: "",
    message: "",
    attachments: []
  });
  
  const [needsTires, setNeedsTires] = useState(true);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Fetch testimonials
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        setTestimonials(response.data);
      } catch (error) {
        console.error("Error fetching testimonials:", error);
      }
    };
    fetchTestimonials();
  }, []);

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} est trop volumineux (max 5MB)`);
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, reader.result]
        }));
      };
      reader.readAsDataURL(file);
    });
    
    // Reset input
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const updateCustomer = (field, value) => {
    setFormData(prev => ({
      ...prev,
      customer: { ...prev.customer, [field]: value }
    }));
  };

  const updateVehicle = (field, value) => {
    setFormData(prev => ({
      ...prev,
      vehicle: { ...prev.vehicle, [field]: value }
    }));
  };

  const updateTires = (field, value) => {
    setFormData(prev => ({
      ...prev,
      tires: { ...prev.tires, [field]: value }
    }));
  };

  const updateServices = (field, value) => {
    setFormData(prev => ({
      ...prev,
      services: { ...prev.services, [field]: value }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer.first_name || !formData.customer.last_name || !formData.customer.email || !formData.customer.phone) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setLoading(true);
    
    try {
      const payload = {
        ...formData,
        tires: needsTires ? formData.tires : null
      };
      
      const response = await axios.post(`${API}/quotes`, payload);
      
      if (response.data.success) {
        setSubmitted(true);
        toast.success("Demande envoyée avec succès !");
      }
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('quote-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
        <div className="text-center max-w-lg animate-fade-in-up">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">MERCI !</h1>
          <p className="text-zinc-400 text-lg mb-8">
            Votre demande de devis a été envoyée avec succès. Notre équipe vous contactera dans les plus brefs délais.
          </p>
          <Button 
            onClick={() => { setSubmitted(false); setFormData({
              customer: { first_name: "", last_name: "", email: "", phone: "", address: "", city: "", postal_code: "" },
              vehicle: { make: "", model: "", year: "", license_plate: "" },
              tires: { width: "", aspect_ratio: "", diameter: "", brand_preference: "", tire_type: "", quantity: 4, additional_notes: "" },
              services: { oil_change: false, oil_filter: false, air_filter: false, cabin_filter: false, brake_discs: false, brake_pads: false, diagnostic: false, other_services: "" },
              preferred_date: "", message: "", attachments: []
            }); }}
            className="bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-widest px-8 py-4 btn-mechanical"
            data-testid="new-quote-btn"
          >
            Nouvelle demande
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3 logo-hover" data-testid="logo-link">
            <img src={LOGO_URL} alt="PP Performance" className="h-12 w-auto" />
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#services" className="text-zinc-400 hover:text-white uppercase tracking-widest text-sm font-medium transition-colors" data-testid="nav-services">Services</a>
            <a href="#quote-form" className="text-zinc-400 hover:text-white uppercase tracking-widest text-sm font-medium transition-colors" data-testid="nav-quote">Devis</a>
            <a href="/admin" className="text-zinc-400 hover:text-white uppercase tracking-widest text-sm font-medium transition-colors" data-testid="nav-admin">Admin</a>
          </nav>
          <div className="flex items-center gap-3">
            <a 
              href="/admin" 
              className="md:hidden text-zinc-400 hover:text-white p-2"
              data-testid="mobile-admin-link"
              title="Administration"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            </a>
            <Button 
              onClick={scrollToForm}
              className="bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-widest px-4 md:px-6 py-2 text-xs md:text-sm btn-mechanical"
              data-testid="header-cta-btn"
            >
              <span className="hidden sm:inline">Demander un devis</span>
              <span className="sm:hidden">Devis</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1735012314840-af32b19e2252?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBzcG9ydHMlMjBjYXIlMjB3aGVlbCUyMGNsb3NlJTIwdXAlMjBkYXJrJTIwYWVzdGhldGljfGVufDB8fHx8MTc3MTU3MjMxNHww&ixlib=rb-4.1.0&q=85)` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 noise-overlay" />
        
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <h1 className="text-6xl md:text-8xl font-black tracking-tighter uppercase mb-6 animate-fade-in-up">
            PP PERFORMANCE
          </h1>
          <p className="text-xl md:text-2xl text-zinc-400 mb-8 max-w-2xl mx-auto animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
            Expert en pneumatiques et entretien automobile. Qualité, performance, confiance.
          </p>
          <Button 
            onClick={scrollToForm}
            className="bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-widest px-10 py-5 text-lg btn-mechanical animate-pulse-glow animate-fade-in-up stagger-2"
            style={{ opacity: 0 }}
            data-testid="hero-cta-btn"
          >
            Obtenir un devis gratuit
          </Button>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-zinc-500" />
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <p className="text-sm font-medium tracking-widest uppercase text-zinc-500 mb-2">Nos services</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase">Ce que nous proposons</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Pneus */}
            <div className="service-card bg-zinc-900/30 border border-white/5 p-8 card-hover" data-testid="service-tires">
              <div className="w-14 h-14 bg-white/5 flex items-center justify-center mb-6">
                <Car className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 tracking-wide">PNEUMATIQUES</h3>
              <ul className="space-y-3 text-zinc-400">
                <li>Vente de pneus toutes marques</li>
                <li>Montage et équilibrage</li>
                <li>Pneus été, hiver, 4 saisons</li>
                <li>Conseil personnalisé</li>
              </ul>
            </div>

            {/* Entretien */}
            <div className="service-card bg-zinc-900/30 border border-white/5 p-8 card-hover" data-testid="service-maintenance">
              <div className="w-14 h-14 bg-white/5 flex items-center justify-center mb-6">
                <Wrench className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 tracking-wide">ENTRETIEN</h3>
              <ul className="space-y-3 text-zinc-400">
                <li>Vidange moteur</li>
                <li>Changement de filtres</li>
                <li>Disques et plaquettes de frein</li>
                <li>Diagnostic véhicule</li>
              </ul>
            </div>

            {/* Expertise */}
            <div className="service-card bg-zinc-900/30 border border-white/5 p-8 card-hover" data-testid="service-expertise">
              <div className="w-14 h-14 bg-white/5 flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 tracking-wide">EXPERTISE</h3>
              <ul className="space-y-3 text-zinc-400">
                <li>Devis gratuit et rapide</li>
                <li>Prix compétitifs</li>
                <li>Service professionnel</li>
                <li>Satisfaction garantie</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section id="testimonials" className="py-24 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-16 text-center">
              <p className="text-sm font-medium tracking-widest uppercase text-zinc-500 mb-2">Témoignages</p>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase">Ce que disent nos clients</h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.slice(0, 6).map((testimonial, index) => (
                <div 
                  key={testimonial.id || index} 
                  className="bg-zinc-900/30 border border-white/5 p-8 card-hover relative"
                  data-testid={`testimonial-${index}`}
                >
                  <Quote className="w-10 h-10 text-zinc-700 absolute top-6 right-6" />
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`w-5 h-5 ${i < testimonial.rating ? 'text-yellow-500 fill-yellow-500' : 'text-zinc-700'}`} 
                      />
                    ))}
                  </div>
                  <p className="text-zinc-300 mb-6 italic">"{testimonial.comment}"</p>
                  <div className="border-t border-zinc-800 pt-4">
                    <p className="font-bold">{testimonial.client_name}</p>
                    {testimonial.vehicle && (
                      <p className="text-zinc-500 text-sm">{testimonial.vehicle}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Quote Form Section */}
      <section id="quote-form" className="py-24 px-6 bg-zinc-900/20">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12 text-center">
            <p className="text-sm font-medium tracking-widest uppercase text-zinc-500 mb-2">Demande de devis</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase">Obtenez votre devis</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-12" data-testid="quote-form">
            {/* Customer Info */}
            <div className="form-section pl-6">
              <h3 className="text-xl font-bold uppercase tracking-wide mb-6">Vos coordonnées</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Prénom *</Label>
                  <Input 
                    value={formData.customer.first_name}
                    onChange={(e) => updateCustomer("first_name", e.target.value)}
                    className="bg-zinc-900/50 border-zinc-800 focus:border-white input-glow"
                    placeholder="Jean"
                    required
                    data-testid="input-first-name"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Nom *</Label>
                  <Input 
                    value={formData.customer.last_name}
                    onChange={(e) => updateCustomer("last_name", e.target.value)}
                    className="bg-zinc-900/50 border-zinc-800 focus:border-white input-glow"
                    placeholder="Dupont"
                    required
                    data-testid="input-last-name"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Email *</Label>
                  <Input 
                    type="email"
                    value={formData.customer.email}
                    onChange={(e) => updateCustomer("email", e.target.value)}
                    className="bg-zinc-900/50 border-zinc-800 focus:border-white input-glow"
                    placeholder="jean.dupont@email.com"
                    required
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Téléphone *</Label>
                  <Input 
                    type="tel"
                    value={formData.customer.phone}
                    onChange={(e) => updateCustomer("phone", e.target.value)}
                    className="bg-zinc-900/50 border-zinc-800 focus:border-white input-glow"
                    placeholder="06 12 34 56 78"
                    required
                    data-testid="input-phone"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Adresse</Label>
                  <Input 
                    value={formData.customer.address}
                    onChange={(e) => updateCustomer("address", e.target.value)}
                    className="bg-zinc-900/50 border-zinc-800 focus:border-white input-glow"
                    placeholder="123 Rue de la République"
                    data-testid="input-address"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Ville</Label>
                  <Input 
                    value={formData.customer.city}
                    onChange={(e) => updateCustomer("city", e.target.value)}
                    className="bg-zinc-900/50 border-zinc-800 focus:border-white input-glow"
                    placeholder="Paris"
                    data-testid="input-city"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Code postal</Label>
                  <Input 
                    value={formData.customer.postal_code}
                    onChange={(e) => updateCustomer("postal_code", e.target.value)}
                    className="bg-zinc-900/50 border-zinc-800 focus:border-white input-glow"
                    placeholder="75001"
                    data-testid="input-postal-code"
                  />
                </div>
              </div>
            </div>

            {/* Vehicle Info */}
            <div className="form-section pl-6">
              <h3 className="text-xl font-bold uppercase tracking-wide mb-6">Votre véhicule</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Marque</Label>
                  <Input 
                    value={formData.vehicle.make}
                    onChange={(e) => updateVehicle("make", e.target.value)}
                    className="bg-zinc-900/50 border-zinc-800 focus:border-white input-glow"
                    placeholder="Peugeot"
                    data-testid="input-vehicle-make"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Modèle</Label>
                  <Input 
                    value={formData.vehicle.model}
                    onChange={(e) => updateVehicle("model", e.target.value)}
                    className="bg-zinc-900/50 border-zinc-800 focus:border-white input-glow"
                    placeholder="308"
                    data-testid="input-vehicle-model"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Année</Label>
                  <Input 
                    value={formData.vehicle.year}
                    onChange={(e) => updateVehicle("year", e.target.value)}
                    className="bg-zinc-900/50 border-zinc-800 focus:border-white input-glow"
                    placeholder="2022"
                    data-testid="input-vehicle-year"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Numéro de châssis (VIN)</Label>
                  <Input 
                    value={formData.vehicle.license_plate}
                    onChange={(e) => updateVehicle("license_plate", e.target.value)}
                    className="bg-zinc-900/50 border-zinc-800 focus:border-white input-glow"
                    placeholder="WVWZZZ3CZWE123456"
                    data-testid="input-vehicle-plate"
                  />
                </div>
              </div>
              
              {/* Document Upload Section */}
              <div className="mt-8">
                <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-3 block">
                  Documents / Photos (optionnel)
                </Label>
                <p className="text-zinc-500 text-sm mb-4">
                  Ajoutez des photos de votre carte grise, certificat de conformité, pneus usés, etc.
                </p>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept="image/*"
                  multiple
                  className="hidden"
                  data-testid="file-input"
                />
                
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-zinc-700 hover:border-zinc-500 bg-zinc-900/30 p-8 text-center cursor-pointer transition-colors group"
                  data-testid="upload-zone"
                >
                  <Upload className="w-10 h-10 text-zinc-500 group-hover:text-zinc-400 mx-auto mb-3 transition-colors" />
                  <p className="text-zinc-400 group-hover:text-zinc-300 font-medium transition-colors">
                    Cliquez pour ajouter des photos
                  </p>
                  <p className="text-zinc-600 text-sm mt-1">
                    JPG, PNG • Max 5MB par fichier
                  </p>
                </div>
                
                {/* Preview uploaded images */}
                {formData.attachments.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {formData.attachments.map((attachment, index) => (
                      <div key={index} className="relative group">
                        <img 
                          src={attachment} 
                          alt={`Document ${index + 1}`}
                          className="w-full h-24 object-cover border border-zinc-700"
                        />
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`remove-attachment-${index}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Tires Section */}
            <div className="form-section pl-6">
              <div className="flex items-center gap-4 mb-6">
                <Checkbox 
                  checked={needsTires}
                  onCheckedChange={setNeedsTires}
                  id="needs-tires"
                  data-testid="checkbox-needs-tires"
                />
                <Label htmlFor="needs-tires" className="text-xl font-bold uppercase tracking-wide cursor-pointer">
                  J'ai besoin de pneus
                </Label>
              </div>
              
              {needsTires && (
                <div className="grid md:grid-cols-3 gap-6 animate-fade-in">
                  <div>
                    <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Largeur</Label>
                    <Select value={formData.tires.width} onValueChange={(v) => updateTires("width", v)}>
                      <SelectTrigger className="bg-zinc-900/50 border-zinc-800" data-testid="select-tire-width">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIRE_WIDTHS.map(w => (
                          <SelectItem key={w} value={w}>{w}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Série</Label>
                    <Select value={formData.tires.aspect_ratio} onValueChange={(v) => updateTires("aspect_ratio", v)}>
                      <SelectTrigger className="bg-zinc-900/50 border-zinc-800" data-testid="select-tire-ratio">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {ASPECT_RATIOS.map(r => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Diamètre</Label>
                    <Select value={formData.tires.diameter} onValueChange={(v) => updateTires("diameter", v)}>
                      <SelectTrigger className="bg-zinc-900/50 border-zinc-800" data-testid="select-tire-diameter">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {DIAMETERS.map(d => (
                          <SelectItem key={d} value={d}>R{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Type de pneu</Label>
                    <Select value={formData.tires.tire_type} onValueChange={(v) => updateTires("tire_type", v)}>
                      <SelectTrigger className="bg-zinc-900/50 border-zinc-800" data-testid="select-tire-type">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIRE_TYPES.map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Marque préférée</Label>
                    <Select value={formData.tires.brand_preference} onValueChange={(v) => updateTires("brand_preference", v)}>
                      <SelectTrigger className="bg-zinc-900/50 border-zinc-800" data-testid="select-tire-brand">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIRE_BRANDS.map(b => (
                          <SelectItem key={b} value={b}>{b}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Quantité</Label>
                    <Select value={String(formData.tires.quantity)} onValueChange={(v) => updateTires("quantity", parseInt(v))}>
                      <SelectTrigger className="bg-zinc-900/50 border-zinc-800" data-testid="select-tire-quantity">
                        <SelectValue placeholder="4" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1</SelectItem>
                        <SelectItem value="2">2</SelectItem>
                        <SelectItem value="4">4</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-3">
                    <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Notes additionnelles</Label>
                    <Textarea 
                      value={formData.tires.additional_notes}
                      onChange={(e) => updateTires("additional_notes", e.target.value)}
                      className="bg-zinc-900/50 border-zinc-800 focus:border-white input-glow"
                      placeholder="Références spécifiques, contraintes, etc."
                      data-testid="textarea-tire-notes"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Services Section */}
            <div className="form-section pl-6">
              <h3 className="text-xl font-bold uppercase tracking-wide mb-6">Services additionnels</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 bg-zinc-900/30 border border-zinc-800 hover:border-zinc-700 transition-colors">
                  <Checkbox 
                    checked={formData.services.oil_change}
                    onCheckedChange={(v) => updateServices("oil_change", v)}
                    id="oil-change"
                    data-testid="checkbox-oil-change"
                  />
                  <Label htmlFor="oil-change" className="cursor-pointer font-medium">Vidange moteur</Label>
                </div>
                <div className="flex items-center gap-3 p-4 bg-zinc-900/30 border border-zinc-800 hover:border-zinc-700 transition-colors">
                  <Checkbox 
                    checked={formData.services.oil_filter}
                    onCheckedChange={(v) => updateServices("oil_filter", v)}
                    id="oil-filter"
                    data-testid="checkbox-oil-filter"
                  />
                  <Label htmlFor="oil-filter" className="cursor-pointer font-medium">Filtre à huile</Label>
                </div>
                <div className="flex items-center gap-3 p-4 bg-zinc-900/30 border border-zinc-800 hover:border-zinc-700 transition-colors">
                  <Checkbox 
                    checked={formData.services.air_filter}
                    onCheckedChange={(v) => updateServices("air_filter", v)}
                    id="air-filter"
                    data-testid="checkbox-air-filter"
                  />
                  <Label htmlFor="air-filter" className="cursor-pointer font-medium">Filtre à air</Label>
                </div>
                <div className="flex items-center gap-3 p-4 bg-zinc-900/30 border border-zinc-800 hover:border-zinc-700 transition-colors">
                  <Checkbox 
                    checked={formData.services.cabin_filter}
                    onCheckedChange={(v) => updateServices("cabin_filter", v)}
                    id="cabin-filter"
                    data-testid="checkbox-cabin-filter"
                  />
                  <Label htmlFor="cabin-filter" className="cursor-pointer font-medium">Filtre d'habitacle</Label>
                </div>
                <div className="flex items-center gap-3 p-4 bg-zinc-900/30 border border-zinc-800 hover:border-zinc-700 transition-colors">
                  <Checkbox 
                    checked={formData.services.brake_discs}
                    onCheckedChange={(v) => updateServices("brake_discs", v)}
                    id="brake-discs"
                    data-testid="checkbox-brake-discs"
                  />
                  <Label htmlFor="brake-discs" className="cursor-pointer font-medium">Disques de frein</Label>
                </div>
                <div className="flex items-center gap-3 p-4 bg-zinc-900/30 border border-zinc-800 hover:border-zinc-700 transition-colors">
                  <Checkbox 
                    checked={formData.services.brake_pads}
                    onCheckedChange={(v) => updateServices("brake_pads", v)}
                    id="brake-pads"
                    data-testid="checkbox-brake-pads"
                  />
                  <Label htmlFor="brake-pads" className="cursor-pointer font-medium">Plaquettes de frein</Label>
                </div>
                <div className="flex items-center gap-3 p-4 bg-zinc-900/30 border border-zinc-800 hover:border-zinc-700 transition-colors">
                  <Checkbox 
                    checked={formData.services.diagnostic}
                    onCheckedChange={(v) => updateServices("diagnostic", v)}
                    id="diagnostic"
                    data-testid="checkbox-diagnostic"
                  />
                  <Label htmlFor="diagnostic" className="cursor-pointer font-medium">Diagnostic véhicule</Label>
                </div>
              </div>
              <div className="mt-6">
                <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Autres services demandés</Label>
                <Textarea 
                  value={formData.services.other_services}
                  onChange={(e) => updateServices("other_services", e.target.value)}
                  className="bg-zinc-900/50 border-zinc-800 focus:border-white input-glow"
                  placeholder="Décrivez les autres services dont vous avez besoin..."
                  data-testid="textarea-other-services"
                />
              </div>
            </div>

            {/* Additional Info */}
            <div className="form-section pl-6">
              <h3 className="text-xl font-bold uppercase tracking-wide mb-6">Informations complémentaires</h3>
              <div className="space-y-6">
                <div>
                  <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Date souhaitée</Label>
                  <Input 
                    type="date"
                    value={formData.preferred_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, preferred_date: e.target.value }))}
                    className="bg-zinc-900/50 border-zinc-800 focus:border-white input-glow max-w-xs"
                    data-testid="input-preferred-date"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">Message</Label>
                  <Textarea 
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    className="bg-zinc-900/50 border-zinc-800 focus:border-white input-glow"
                    placeholder="Informations supplémentaires, questions, contraintes horaires..."
                    rows={4}
                    data-testid="textarea-message"
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="text-center pt-6">
              <Button 
                type="submit"
                disabled={loading}
                className="bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-widest px-12 py-6 text-lg btn-mechanical animate-pulse-glow disabled:opacity-50"
                data-testid="submit-quote-btn"
              >
                {loading ? "Envoi en cours..." : "Envoyer ma demande"}
              </Button>
              <p className="text-zinc-500 text-sm mt-4">
                Nous vous recontacterons dans les 24h avec un devis personnalisé.
              </p>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12">
            <div>
              <img src={LOGO_URL} alt="PP Performance" className="h-16 mb-6" />
              <p className="text-zinc-500">
                Expert en pneumatiques et entretien automobile au Luxembourg.
              </p>
            </div>
            <div>
              <h4 className="font-bold uppercase tracking-wider mb-4">Services</h4>
              <ul className="space-y-2 text-zinc-400">
                <li>Vente et montage de pneus</li>
                <li>Vidange et filtres</li>
                <li>Freinage</li>
                <li>Diagnostic</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold uppercase tracking-wider mb-4">Contact</h4>
              <ul className="space-y-3 text-zinc-400">
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <a href="tel:+352661906002" className="hover:text-white transition-colors">+352 661 906 002</a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <a href="mailto:pp.performance25@gmail.com" className="hover:text-white transition-colors">pp.performance25@gmail.com</a>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>Rodange, Luxembourg</span>
                </li>
                <li className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>Sur rendez-vous</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-zinc-800 mt-12 pt-8 text-center text-zinc-500 text-sm">
            © 2024 PP Performance. Tous droits réservés.
          </div>
        </div>
      </footer>
    </div>
  );
}
